<template>
  <div>
    <div v-if="!hq" class="text-center py-5 text-secondary">
      Create the HQ budget first (HQ Budget tab) to track expenses for {{ selectedYear }}.
    </div>

    <div v-else>
      <div class="d-flex justify-content-end mb-3">
        <button class="btn btn-sm btn-primary" @click="openMonth()">
          <i class="fa-solid fa-plus me-1" /> Add expense
        </button>
      </div>

      <loading-spinner v-if="isLoading" />

      <!-- District-breakable: case-derived actuals (read-only) -->
      <div class="mb-4">
        <h6 class="text-secondary mb-3">NOD / Emergency / Electrical</h6>
        <div class="row g-3">
          <div class="col-md-4" v-for="s in autoSummary" :key="s.category">
            <div class="p-3 rounded bg-semi-transparent h-100 border-start border-4"
              :class="s.remaining < 0 ? 'border-danger' : 'border-success'">
              <div class="fw-bold mb-2">{{ s.label }}</div>
              <div class="d-flex justify-content-between small">
                <span class="text-secondary">Allocated</span><span>{{ formatMoney(s.allocated) }}</span>
              </div>
              <div class="d-flex justify-content-between small">
                <span class="text-secondary">Used</span>
                <span>{{ formatMoney(s.spent) }} <span class="text-secondary">({{ s.pct.toFixed(1) }}%)</span></span>
              </div>
              <hr class="my-2" />
              <div class="d-flex justify-content-between">
                <span>Remaining</span>
                <strong :class="s.remaining < 0 ? 'text-danger' : 'text-success'">{{ formatMoney(s.remaining) }} ({{ s.remainingPct.toFixed(1) }}%)</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Manual categories: matrix of category x month, with cumulative totals -->
      <div>
        <h6 class="text-secondary mb-2">Other categories</h6>
        <p class="small text-secondary mb-2">Click a month cell to add or edit that month's entries.</p>
        <div class="table-responsive hide-scrollbar">
          <table class="table table-dark table-sm align-middle text-nowrap mb-0 expense-matrix">
            <thead>
              <tr>
                <th class="cat-col">Category</th>
                <th v-for="m in months" :key="`h-${m.num}`" class="text-end">{{ m.label }}</th>
                <th class="text-end used-col">Used</th>
                <th class="text-end alloc-col">Allocated</th>
                <th class="text-end remain-col">Remaining</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in manualMatrix">
                <tr :key="row.category">
                  <td class="fw-semibold cat-col" :style="row.isKerjaRutin ? 'cursor: pointer' : null"
                    @click="row.isKerjaRutin ? (kerjaRutinExpanded = !kerjaRutinExpanded) : null">
                    <i v-if="row.isKerjaRutin" class="fas me-2" :class="kerjaRutinExpanded ? 'fa-chevron-down' : 'fa-chevron-right'" />{{ row.label }}
                  </td>
                  <td v-for="m in months" :key="`${row.category}-${m.num}`" class="text-end"
                    style="cursor: pointer" @click="openMonth(m.num)">
                    <span v-if="row.months[m.num]">{{ formatMoney(row.months[m.num]) }}</span>
                    <span v-else class="text-secondary">&ndash;</span>
                  </td>
                  <td class="text-end fw-semibold used-col">
                    {{ formatMoney(row.used) }}
                    <div class="small text-secondary fw-normal">{{ pct(row.used, row.allocated) }}</div>
                  </td>
                  <td class="text-end alloc-col">
                    {{ formatMoney(row.allocated) }}
                  </td>
                  <td class="text-end remain-col" :class="row.remaining < 0 ? 'text-danger' : 'text-success'">
                    {{ formatMoney(row.remaining) }}
                  </td>
                </tr>
                <template v-if="row.isKerjaRutin && kerjaRutinExpanded">
                  <tr v-for="dr in kerjaRutinDistrictRows" :key="`kr-${dr.id}`" class="text-secondary">
                    <td class="ps-5 cat-col">{{ dr.label }}</td>
                    <td v-for="m in months" :key="`kr-${dr.id}-${m.num}`" class="text-end"
                      style="cursor: pointer" @click="openMonth(m.num)">
                      <span v-if="dr.months[m.num]">{{ formatMoney(dr.months[m.num]) }}</span>
                      <span v-else class="text-secondary">&ndash;</span>
                    </td>
                    <td class="text-end used-col">{{ formatMoney(dr.used) }}</td>
                    <td class="text-end alloc-col">&mdash;</td>
                    <td class="text-end remain-col">&mdash;</td>
                  </tr>
                </template>
              </template>
            </tbody>
            <tfoot>
              <tr class="fw-bold">
                <td class="cat-col">Total</td>
                <td v-for="m in months" :key="`t-${m.num}`" class="text-end">{{ formatMoney(monthTotals[m.num]) }}</td>
                <td class="text-end used-col">
                  {{ formatMoney(grandUsed) }}
                  <div class="small text-secondary fw-normal">{{ pct(grandUsed, grandAllocated) }}</div>
                </td>
                <td class="text-end alloc-col">
                  {{ formatMoney(grandAllocated) }}
                </td>
                <td class="text-end remain-col" :class="grandRemaining < 0 ? 'text-danger' : 'text-success'">
                  {{ formatMoney(grandRemaining) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <add-budget-expense-modal
      :show="showModal"
      :hq-id="hq && hq.id"
      :year="selectedYear || Helper.currentYear"
      :preset-month="presetMonth"
      :expenses="expenses"
      :districts="districts"
      @close="closeModal"
    />
  </div>
</template>

<script>
import LoadingSpinner from "@/components/LoadingSpinner";
import AddBudgetExpenseModal from "@/components/AddBudgetExpenseModal";
import {
  DISTRICT_CATEGORIES,
  EXPENSE_CATEGORIES,
  CATEGORY_LABELS,
  getAmount,
  caseActual,
} from "@/utils/budget";

export default {
  name: "expenses-tab",
  props: ["selectedYear", "hq", "lines"],
  components: { LoadingSpinner, AddBudgetExpenseModal },
  data() {
    return {
      DISTRICT_CATEGORIES,
      EXPENSE_CATEGORIES,
      CATEGORY_LABELS,
      isLoading: false,
      expenses: [],
      cases: [],
      districts: [],
      showModal: null,
      presetMonth: null,
      kerjaRutinExpanded: true,
    };
  },
  watch: {
    selectedYear: {
      handler() {
        this.loadAll();
      },
      immediate: false,
    },
    hq: {
      handler() {
        this.loadAll();
      },
      immediate: false,
    },
  },
  computed: {
    formatMoney() {
      return (val) => this.Helper.formatMoney(val);
    },
    // 12 month columns with short labels (Jan…Dec) for the selected year.
    months() {
      const year = this.selectedYear || this.Helper.currentYear;
      return Array.from({ length: 12 }, (_, i) => ({
        num: i + 1,
        label: this.Helper.moment(`${year}-${String(i + 1).padStart(2, "0")}-01`).format("MMM"),
      }));
    },
    // One row per manual category: per-month amounts + cumulative used / allocated / remaining.
    manualMatrix() {
      return EXPENSE_CATEGORIES.map((cat) => {
        const months = {};
        let used = 0;
        this.expenses
          .filter((e) => e.category === cat)
          .forEach((e) => {
            const amt = Number(e.amount) || 0;
            months[e.month] = (months[e.month] || 0) + amt;
            used += amt;
          });
        const allocated = this.ceiling(cat);
        return {
          category: cat,
          label: CATEGORY_LABELS[cat],
          isKerjaRutin: cat === "kerja_rutin",
          months,
          used,
          allocated,
          remaining: allocated - used,
        };
      });
    },
    // Per-district breakdown for Kerja Rutin's row above — grouped straight
    // from `expenses` (this tab already loads exactly one HQ+year's worth, so
    // no extra year/scope filtering is needed here, unlike dashboard.js). Only
    // districts (or "Unassigned") that actually have an entry appear; there is
    // no per-district Allocated/Remaining, only the parent row's HQ totals.
    kerjaRutinDistrictRows() {
      const byDistrict = {};
      this.expenses
        .filter((e) => e.category === "kerja_rutin")
        .forEach((e) => {
          const id = e.district && e.district.id != null ? e.district.id : "unassigned";
          const label = e.district && e.district.name ? e.district.name : "Unassigned";
          if (!byDistrict[id]) byDistrict[id] = { id, label, months: {}, used: 0 };
          const amt = Number(e.amount) || 0;
          byDistrict[id].months[e.month] = (byDistrict[id].months[e.month] || 0) + amt;
          byDistrict[id].used += amt;
        });
      // Always show every real district (0/"–" when it has no entries yet)
      // so the breakdown structure is visible before any data exists;
      // "Unassigned" only appears when there's actual legacy no-district
      // data to show/edit.
      const ordered = this.districts.map((d) => byDistrict[d.id] || { id: d.id, label: d.name, months: {}, used: 0 });
      if (byDistrict.unassigned) ordered.push(byDistrict.unassigned);
      return ordered;
    },
    // Column totals across all manual categories (sum per month).
    monthTotals() {
      const totals = {};
      this.months.forEach((m) => (totals[m.num] = 0));
      this.expenses.forEach((e) => {
        if (totals[e.month] != null) totals[e.month] += Number(e.amount) || 0;
      });
      return totals;
    },
    grandUsed() {
      return this.manualMatrix.reduce((sum, r) => sum + r.used, 0);
    },
    grandAllocated() {
      return this.manualMatrix.reduce((sum, r) => sum + r.allocated, 0);
    },
    grandRemaining() {
      return this.grandAllocated - this.grandUsed;
    },
    autoSpent() {
      const year = Number(this.selectedYear || this.Helper.currentYear);
      const districtIds = [...new Set(this.cases.map((c) => c.district))];
      const out = {};
      DISTRICT_CATEGORIES.forEach((cat) => {
        out[cat] = districtIds.reduce(
          (sum, did) =>
            sum +
            caseActual(this.cases, {
              category: cat,
              districtId: did,
              year,
              moment: this.Helper.moment,
            }),
          0
        );
      });
      return out;
    },
    // Per district-breakable category: HQ allocation, case-driven spend, and remaining.
    autoSummary() {
      return DISTRICT_CATEGORIES.map((cat) => {
        const allocated = this.ceiling(cat);
        const spent = this.autoSpent[cat] || 0;
        return {
          category: cat,
          label: CATEGORY_LABELS[cat],
          allocated,
          spent,
          remaining: allocated - spent,
          pct: allocated > 0 ? (spent / allocated) * 100 : 0, // % of allocation used
          remainingPct: allocated > 0 ? ((allocated - spent) / allocated) * 100 : 0, // % left
        };
      });
    },
  },
  methods: {
    ceiling(category) {
      return getAmount(this.lines, category);
    },
    // Format part/whole as a 1-decimal percentage (0.0% when whole is 0).
    pct(part, whole) {
      const p = whole > 0 ? (part / whole) * 100 : 0;
      return `${p.toFixed(1)}%`;
    },
    // Open the monthly editor (all categories for one month). With a month -> that
    // month is pre-selected and locked; without -> the month is selectable.
    openMonth(month) {
      this.presetMonth = month || null;
      this.showModal = true;
    },
    closeModal(reload) {
      this.showModal = false;
      this.presetMonth = null;
      if (reload) this.loadExpenses();
    },
    async loadExpenses() {
      if (!this.hq) {
        this.expenses = [];
        return;
      }
      const [call] = await this.Helper.handlePromise(
        this.API.get(`nod-budget-expenses?hq=${this.hq.id}&_limit=-1`)
      );
      this.expenses = call && call.data ? call.data : [];
    },
    async loadCases() {
      const url = this.API.endpoints(this.selectedYear).allCases;
      const [call] = await this.Helper.handlePromise(this.API.get(url));
      this.cases = call && call.data ? call.data.filter((d) => !d.isRejected) : [];
    },
    async loadDistricts() {
      const [call] = await this.Helper.handlePromise(this.API.get(this.API.endpoints().allDistricts));
      this.districts = call && call.data ? call.data : [];
    },
    async loadAll() {
      this.isLoading = true;
      await Promise.all([this.loadExpenses(), this.loadCases(), this.loadDistricts()]);
      this.isLoading = false;
    },
  },
  async mounted() {
    await this.loadAll();
  },
};
</script>

<style scoped>
/* Pin Category (left) and Used/Allocated/Remaining (right); only the month
   columns scroll horizontally inside the table's own scroll area. */
.expense-matrix {
  border-collapse: separate;
  border-spacing: 0;
}
/* Keep the month columns scrollable but hide the scrollbar chrome. */
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  width: 0;
  height: 0;
}
.expense-matrix th,
.expense-matrix td {
  background-color: #212529;
  border-right: 1px solid #495057;
  border-bottom: 1px solid #495057;
}
.expense-matrix thead th {
  border-top: 1px solid #495057;
}
.expense-matrix tr > :first-child {
  border-left: 1px solid #495057;
}
.expense-matrix tfoot th,
.expense-matrix tfoot td {
  background-color: #2c3034;
}
.expense-matrix .cat-col,
.expense-matrix .used-col,
.expense-matrix .alloc-col,
.expense-matrix .remain-col {
  position: sticky;
  z-index: 2;
  background-color: #1a1d20; /* darker so pinned columns read as fixed */
}
.expense-matrix tfoot .cat-col,
.expense-matrix tfoot .used-col,
.expense-matrix tfoot .alloc-col,
.expense-matrix tfoot .remain-col {
  background-color: #25292d;
}
.expense-matrix .cat-col {
  left: 0;
  z-index: 3;
  width: 190px;
  min-width: 190px;
  /* shadow on the right edge cues that this column is frozen */
  box-shadow: 6px 0 8px -6px rgba(0, 0, 0, 0.7);
}
.expense-matrix .used-col {
  /* shadow on the left edge of the frozen summary block */
  box-shadow: -6px 0 8px -6px rgba(0, 0, 0, 0.7);
}
.expense-matrix .used-col {
  right: 300px;
  width: 150px;
  min-width: 150px;
}
.expense-matrix .alloc-col {
  right: 150px;
  width: 150px;
  min-width: 150px;
}
.expense-matrix .remain-col {
  right: 0;
  width: 150px;
  min-width: 150px;
}
.expense-matrix thead .cat-col,
.expense-matrix thead .used-col,
.expense-matrix thead .alloc-col,
.expense-matrix thead .remain-col {
  z-index: 4;
}
</style>
